/**
 * Workbook Integrations: the apps that plug into a Workbook, each declaring
 * where it surfaces instead of being wired in ad hoc. The frontend and backend
 * each keep a registry keyed by these ids and implement exactly the surfaces
 * declared here; a test on each side holds them to it.
 */
export const WORKBOOK_INTEGRATION_SURFACES = [
  /** A section of the Workbook's Automations tab. */
  "automations",
  /** An action on each Run in Run History. */
  "run-actions",
  /** An action on a Process step (Instruction). */
  "process-step",
  /** A backend hook after a Run reaches a terminal status. */
  "run-finished",
] as const;
export type WorkbookIntegrationSurface =
  (typeof WORKBOOK_INTEGRATION_SURFACES)[number];

export type WorkbookIntegrationId = "workflows" | "tasks" | "agents" | "sinks";

export type WorkbookIntegration = {
  id: WorkbookIntegrationId;
  surfaces: readonly WorkbookIntegrationSurface[];
};

export const WORKBOOK_INTEGRATIONS = [
  { id: "workflows", surfaces: ["automations", "run-finished"] },
  {
    id: "tasks",
    surfaces: ["automations", "process-step", "run-actions", "run-finished"],
  },
  { id: "agents", surfaces: ["automations", "run-actions"] },
  /** Data sinks: delivers each Data sink Output that asks for it after an entire Run. */
  { id: "sinks", surfaces: ["run-finished"] },
] as const satisfies readonly WorkbookIntegration[];

/** The integrations that surface at one place, in declaration order. */
export function integrationsAt(
  surface: WorkbookIntegrationSurface,
): WorkbookIntegrationId[] {
  return WORKBOOK_INTEGRATIONS.filter((integration) =>
    (integration.surfaces as readonly WorkbookIntegrationSurface[]).includes(
      surface,
    ),
  ).map((integration) => integration.id);
}

type DeclaredIntegration = (typeof WORKBOOK_INTEGRATIONS)[number];
/**
 * The ids of the integrations that declare a surface. A registry typed
 * `Record<IntegrationsWith<S>, …>` must implement exactly those, so a
 * declaration and its implementations can't drift apart.
 */
export type IntegrationsWith<S extends WorkbookIntegrationSurface> =
  DeclaredIntegration extends infer Integration
    ? Integration extends {
        id: infer Id;
        surfaces: readonly (infer Surface)[];
      }
      ? S extends Surface
        ? Id
        : never
      : never
    : never;
